'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { spring, springSoft } from '@/lib/motion';
import s from './IndustryHero.module.css';

/**
 * IndustryHero — the single, shared hero for every /industries/* page.
 *
 * Consolidates the 12 former per-page hero components (TelecomHero + 11
 * identical "metrics" heroes) into one. Two layout modes:
 *
 *   layout="visual"  (Telecom benchmark)
 *     2-col: copy column (pill breadcrumb, title, lead, actions, trust row)
 *     + right-side `visual` slot (e.g. <TelecomCommandCentre/>).
 *
 *   layout="metrics" (the other 11)
 *     full-width two-tone title, then a bottom row: featured dark stat card
 *     + p-hero paragraph + two stat blocks. Slim text breadcrumb.
 *
 * Per-page copy (verbatim, i18n via t() at the call site) and per-page data
 * (featured / stats / visual / trust) are passed in as props — the component
 * owns only layout, motion, and the background shell.
 *
 * Props:
 *   layout      "visual" | "metrics" (default "metrics")
 *   gridStyle   "grid" | "dots" — background pattern (default per layout)
 *   breadcrumb  { label, href, current }  — label links to href, current is the page
 *   title       headline node (wrap accent words in your own colored <span>)
 *   — visual mode —
 *   lead        lede node
 *   actions     CTA node (use <BookDemoButton/> + ghost <Button/>)
 *   trust       { label, logos: string[] }
 *   visual      right-column node
 *   — metrics mode —
 *   featured    { pre, n, post, src }  — the dark headline-stat card
 *   pHero       paragraph node (className "p-hero" is applied here)
 *   stats       [{ n, lbl, src }]      — text stat blocks (typically 2)
 */
export default function IndustryHero({
  layout = 'metrics',
  gridStyle,
  breadcrumb,
  title,
  // visual mode
  lead,
  actions,
  trust,
  visual,
  // metrics mode
  featured,
  pHero,
  stats = [],
}) {
  const isVisual = layout === 'visual';
  const grid = gridStyle || (isVisual ? 'grid' : 'dots');

  return (
    <section className={s.hero} data-layout={layout}>
      <div aria-hidden className={s.heroBg}>
        <div className={s.orb1} />
        <div className={s.orb2} />
        <div className={grid === 'grid' ? s.ambientGrid : s.dottedGrid} />
      </div>

      {isVisual ? (
        <div className={s.inner}>
          <div className={s.grid}>
            <div className={s.colLeft}>
              {breadcrumb && (
                <nav aria-label={breadcrumb.ariaLabel || breadcrumb.current} className={s.crumbPill}>
                  <span className={s.statusDot} aria-hidden="true" />
                  <Link href={breadcrumb.href} className={s.crumbLink}>
                    {breadcrumb.label}
                  </Link>
                  <span className={s.crumbSep} aria-hidden="true">&gt;</span>
                  <span className={s.crumbAccent}>{breadcrumb.current}</span>
                </nav>
              )}

              <motion.h1
                className={s.title}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ ...spring, delay: 0.05 }}
              >
                {title}
              </motion.h1>

              {lead && (
                <motion.p
                  className={s.lead}
                  initial={{ y: 16, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ ...springSoft, delay: 0.12 }}
                >
                  {lead}
                </motion.p>
              )}

              {actions && (
                <motion.div
                  className={s.actions}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ ...springSoft, delay: 0.18 }}
                >
                  {actions}
                </motion.div>
              )}

              {trust && (
                <motion.div
                  className={s.trustRow}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ ...springSoft, delay: 0.24 }}
                >
                  <span className={s.trustLabel}>{trust.label}</span>
                  <ul className={s.trustLogos} aria-label={trust.label}>
                    {trust.logos.map((name) => (
                      <li key={name} className={s.trustLogo}>{name}</li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>

            {visual && (
              <motion.div
                className={s.colRight}
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ ...springSoft, delay: 0.16 }}
              >
                {visual}
              </motion.div>
            )}
          </div>
        </div>
      ) : (
        <>
          {breadcrumb && (
            <div className={`container ${s.crumbRow}`}>
              <nav aria-label={breadcrumb.ariaLabel || breadcrumb.current} className={s.crumb}>
                <Link href={breadcrumb.href} className={s.crumbLink}>
                  {breadcrumb.label}
                </Link>
                <span className={s.crumbSep} aria-hidden="true">›</span>
                <span className={s.crumbCurrent}>{breadcrumb.current}</span>
              </nav>
            </div>
          )}

          <div className={`container ${s.heroInner}`}>
            <motion.h1
              className={s.title}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...spring, delay: 0.05 }}
            >
              {title}
            </motion.h1>
          </div>

          <div className={`container ${s.bottomRow}`}>
            <div className={s.bottomGrid}>
              {featured && (
                <motion.article
                  className={s.featured}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ ...springSoft, delay: 0.15 }}
                >
                  <span className={s.featuredDecor} aria-hidden="true" />
                  <p className={s.featuredPre}>{featured.pre}</p>
                  {/* <div> not <h3>: legacy.css has a global h3 color !important rule. */}
                  <div className={s.featuredN}>{featured.n}</div>
                  <p className={s.featuredPost}>{featured.post}</p>
                  <p className={s.featuredSrc}>{featured.src}</p>
                </motion.article>
              )}

              <motion.div
                className={s.rightCol}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springSoft, delay: 0.2 }}
              >
                {pHero && <p className="p-hero">{pHero}</p>}
                {stats.length > 0 && (
                  <div className={s.statRow}>
                    {stats.map((stat) => (
                      <div key={stat.n} className={s.statBlock}>
                        <p className={s.statLbl}>{stat.lbl}</p>
                        <div className={s.statN}>{stat.n}</div>
                        <p className={s.statSrc}>{stat.src}</p>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
